import {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from 'react';
import { AlertTriangle, Check, Link, MoreVertical, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserAvatar } from '@/components/user-avatar';
import { useNotification } from '@/components/notifications';
import { LoadingSpinner } from '@/components/loading-spinner';
import type {
  RevokeResponse,
  ShareInviteResponse,
  ShareListEntry,
  ShareListResponse,
} from '@/types/share-types';
import { AuthContext } from '@/context/auth-context';
import { VIEW_GRAPH_PARAM } from '@/components/context-graph-viewer/helpers/view-state';
import {
  shareWorkflow,
  listWorkflowAccess,
  revokeWorkflowAccess,
} from '@/services/workflow/share-workflow';
import {
  shareCollection,
  listCollectionShare,
  revokeCollectionAccess,
  updateCollectionRole,
} from '@/services/data-sources/share-collection';
import {
  shareContextGraph,
  listContextGraphAccess,
  revokeContextGraphAccess,
  updateContextGraphRole,
} from '@/services/context-graphs/share-context-graph';
import * as Sentry from '@sentry/react';
import { validateEmail } from '@/utils/validate-email';
import {
  DefaultAccessDropdown,
  ROLE_DISPLAY_NAMES,
} from '@/components/default-access-dropdown';
import { getAllMembersByOrg } from '@/services/members/get-all-members-by-org';
import type { Member } from '@/types';
import type { AccessRequest, DefaultRoleOption } from '@/types/share-types';
import {
  listAccessRequests,
  reviewAccessRequest,
} from '@/services/workflow/manage-access-requests';
import { setWorkflowDefaultRole } from '@/services/workflow/workflow-default-role';
import { orgPath } from '@/utils/org-path';
import { ROUTES } from '@/constants/routes';
import {
  shareArtifact,
  listArtifactShares,
  revokeArtifactShare,
} from '@/services/artifacts/share-artifact';
import {
  shareConversation,
  listConversationAccess,
  revokeConversationAccess,
} from '@/services/agents/share-conversation';

export type ShareModalResourceType =
  | 'workflow'
  | 'collection'
  | 'context-graph'
  | 'artifact'
  | 'chat';

type GrantableRole = 'viewer' | 'editor';

interface ResourceShareConfig {
  title: string;
  noun: string;
  /**
   * Omitted when the URL conveys no access on its own. Without it the modal
   * hides its copy-link affordances, so the owner is never told they have
   * shared something when only an explicit invite does that.
   */
  buildLink?: (resourceId: string) => string;
  api: {
    list: (orgId: string, resourceId: string) => Promise<ShareListResponse>;
    share: (
      orgId: string,
      resourceId: string,
      emails: string[],
      role: GrantableRole,
    ) => Promise<ShareInviteResponse>;
    revoke: (
      orgId: string,
      resourceId: string,
      email: string,
    ) => Promise<RevokeResponse>;
    updateRole?: (
      orgId: string,
      resourceId: string,
      email: string,
      role: GrantableRole,
    ) => Promise<void>;
  };
  /** Grant-based ACL with inline viewer/editor role management. */
  rolesManaged: boolean;
  supportsShareWithOrg: boolean;
  supportsAccessRequests: boolean;
  supportsDefaultRole: boolean;
  /**
   * Per-resource overrides for the role label in the access list, for
   * resources whose roles do not mean what ROLE_DISPLAY_NAMES says -- e.g.
   * a workflow viewer is "Discoverable" (listed, not readable).
   */
  roleDisplayNames?: Record<string, string>;
}

// Workflow/artifact services predate the throwing contract and still return
// {detail} unions; these adapters normalize them so the modal has one error
// path per action. A workflow/artifact revoke that reports status 'failed'
// stays data (it carries a per-email reason), not an exception.
const throwOnDetail = <T extends object>(result: T | { detail: string }): T => {
  if (
    'detail' in result &&
    result.detail &&
    !('status' in result) &&
    !('shared_with' in result)
  ) {
    throw new Error(result.detail);
  }
  return result as T;
};

const RESOURCE_CONFIG: Record<ShareModalResourceType, ResourceShareConfig> = {
  workflow: {
    title: 'Share this workflow',
    noun: 'workflow',
    buildLink: (resourceId) =>
      `${window.location.origin}${orgPath(`${ROUTES.WORKFLOW.ROOT}/${resourceId}`)}`,
    api: {
      list: listWorkflowAccess,
      share: async (orgId, resourceId, emails) =>
        throwOnDetail(await shareWorkflow(orgId, resourceId, { emails })),
      revoke: async (orgId, resourceId, email) =>
        throwOnDetail(await revokeWorkflowAccess(orgId, resourceId, email)),
    },
    rolesManaged: false,
    supportsShareWithOrg: false,
    supportsAccessRequests: true,
    supportsDefaultRole: true,
    // Workflow viewers are listed but cannot open the workflow.
    roleDisplayNames: { viewer: 'Discoverable' },
  },
  collection: {
    title: 'Share this collection',
    noun: 'collection',
    buildLink: (resourceId) =>
      `${window.location.origin}${orgPath(`${ROUTES.DATA_SOURCES.ROOT}/${resourceId}`)}`,
    api: {
      list: listCollectionShare,
      share: (orgId, resourceId, emails, role) =>
        shareCollection(orgId, resourceId, { emails, role }),
      revoke: revokeCollectionAccess,
      updateRole: updateCollectionRole,
    },
    rolesManaged: true,
    supportsShareWithOrg: false,
    supportsAccessRequests: false,
    supportsDefaultRole: false,
  },
  'context-graph': {
    title: 'Share this context graph',
    noun: 'context graph',
    // The graphs page opens the viewer from ?viewGraph=; there is no
    // /context-graphs/:id route.
    buildLink: (resourceId) =>
      `${window.location.origin}${orgPath(ROUTES.CONTEXT_GRAPHS.ROOT)}?${VIEW_GRAPH_PARAM}=${resourceId}`,
    api: {
      list: listContextGraphAccess,
      share: (orgId, resourceId, emails, role) =>
        shareContextGraph(orgId, resourceId, { emails, role }),
      revoke: revokeContextGraphAccess,
      updateRole: updateContextGraphRole,
    },
    rolesManaged: true,
    supportsShareWithOrg: true,
    supportsAccessRequests: false,
    supportsDefaultRole: false,
  },
  artifact: {
    title: 'Share this artifact',
    noun: 'artifact',
    buildLink: (resourceId) =>
      `${window.location.origin}${orgPath(`${ROUTES.ARTIFACTS}/${resourceId}`)}`,
    api: {
      list: listArtifactShares,
      share: async (orgId, resourceId, emails) =>
        throwOnDetail(
          await shareArtifact(orgId, resourceId, {
            emails,
            role: 'artifact:viewer',
          }),
        ),
      revoke: async (orgId, resourceId, email) =>
        throwOnDetail(await revokeArtifactShare(orgId, resourceId, email)),
    },
    rolesManaged: false,
    supportsShareWithOrg: false,
    supportsAccessRequests: false,
    supportsDefaultRole: false,
  },
  chat: {
    title: 'Share this chat',
    noun: 'chat',
    // No buildLink: a chat URL grants nothing without a grant row, so the
    // modal must not offer to copy one.
    api: {
      list: listConversationAccess,
      share: (orgId, resourceId, emails) =>
        shareConversation(orgId, resourceId, { emails, role: 'viewer' }),
      revoke: revokeConversationAccess,
    },
    rolesManaged: false,
    supportsShareWithOrg: false,
    supportsAccessRequests: false,
    supportsDefaultRole: false,
  },
};

type FetchStatus = 'idle' | 'loading' | 'success' | 'error';

function isOwnerRole(role: string): boolean {
  return role === 'owner' || role.endsWith(':owner');
}

function parseEmails(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

interface ShareModalProps {
  resourceType: ShareModalResourceType;
  resourceId: string;
  orgId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional share link; defaults to current origin + path for workflow or collection */
  shareLink?: string;
  /** Current default role for the workflow (null = private) */
  defaultRole?: DefaultRoleOption;
  /** Called after an access request is successfully accepted or declined */
  onRequestReviewed?: () => void;
  /** Called after the default role is successfully changed */
  onDefaultRoleChange?: (role: DefaultRoleOption) => void;
  /** Show client data warnings when access is set to Editor (LD-gated) */
  showClientDataWarning?: boolean;
}

export function ShareModal({
  resourceType,
  resourceId,
  orgId,
  open,
  onOpenChange,
  shareLink: shareLinkProp,
  defaultRole: defaultRoleProp,
  onRequestReviewed,
  onDefaultRoleChange,
  showClientDataWarning = false,
}: ShareModalProps) {
  const notification = useNotification();
  const { user } = useContext(AuthContext);
  const config = RESOURCE_CONFIG[resourceType];
  const displayRole = (role: string): string => {
    const bareRole = role.includes(':') ? role.split(':')[1] : role;
    return (
      config.roleDisplayNames?.[bareRole] ??
      ROLE_DISPLAY_NAMES[bareRole] ??
      bareRole
    );
  };
  const [emailInput, setEmailInput] = useState('');
  const [emailChips, setEmailChips] = useState<
    { email: string; isValid: boolean }[]
  >([]);
  const [isInviting, setIsInviting] = useState(false);
  const [people, setPeople] = useState<ShareListEntry[]>([]);
  const [listStatus, setListStatus] = useState<FetchStatus>('idle');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [membersStatus, setMembersStatus] = useState<FetchStatus>('idle');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<AccessRequest[]>([]);
  const [reviewingRequestId, setReviewingRequestId] = useState<string | null>(
    null,
  );
  const [currentDefaultRole, setCurrentDefaultRole] =
    useState<DefaultRoleOption>(defaultRoleProp ?? 'workflow:viewer');
  const [isSavingDefaultRole, setIsSavingDefaultRole] = useState(false);
  const [collectionInviteRole, setCollectionInviteRole] = useState<
    'viewer' | 'editor'
  >('viewer');
  const [updatingRoleEmail, setUpdatingRoleEmail] = useState<string | null>(
    null,
  );
  const [isSharingOrg, setIsSharingOrg] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  const { title, noun, rolesManaged: isRoleManaged } = config;

  const defaultLink =
    config.buildLink && typeof window !== 'undefined'
      ? config.buildLink(resourceId)
      : '';
  const shareLink = shareLinkProp ?? defaultLink;

  const fetchPeople = useCallback(async () => {
    if (!orgId || !resourceId) return;
    setListStatus('loading');
    try {
      const result = await config.api.list(orgId, resourceId);
      setPeople(result.access ?? []);
      setListStatus('success');
    } catch (err) {
      Sentry.captureException(err, {
        tags: { share_action: 'list_access', resource_type: resourceType },
        extra: { resourceId, orgId },
      });
      setPeople([]);
      setListStatus('error');
      notification.error('Failed to load the access list');
    }
  }, [orgId, resourceId, resourceType, config, notification]);

  const fetchPendingRequests = useCallback(async () => {
    if (!orgId || !resourceId || !config.supportsAccessRequests) return;
    try {
      const result = await listAccessRequests(orgId, resourceId, 'pending');
      setPendingRequests(result.requests ?? []);
    } catch {
      // Silently fail — user may not have share permission
      setPendingRequests([]);
    }
  }, [orgId, resourceId, config.supportsAccessRequests]);

  const handleReviewRequest = useCallback(
    async (requestId: string, decision: 'approved' | 'denied') => {
      if (!orgId || !resourceId) return;
      setReviewingRequestId(requestId);
      try {
        await reviewAccessRequest(
          orgId,
          resourceId,
          requestId,
          decision,
          decision === 'approved' ? 'workflow:editor' : undefined,
        );
        notification.success(
          decision === 'approved' ? 'Access granted' : 'Request denied',
        );
        fetchPendingRequests();
        fetchPeople();
        onRequestReviewed?.();
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'review-access-request', requestId, decision },
        });
        notification.error('Failed to review request');
      } finally {
        setReviewingRequestId(null);
      }
    },
    [
      orgId,
      resourceId,
      notification,
      fetchPendingRequests,
      fetchPeople,
      onRequestReviewed,
    ],
  );

  const handleDefaultRoleChange = useCallback(
    async (value: DefaultRoleOption) => {
      if (!orgId || !resourceId || !config.supportsDefaultRole) return;
      const role = value;
      setCurrentDefaultRole(role);
      setIsSavingDefaultRole(true);
      try {
        await setWorkflowDefaultRole(orgId, resourceId, role);
        onDefaultRoleChange?.(role);
        notification.success('Default access updated');
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'set-default-role', resourceId, role },
        });
        notification.error('Failed to update default access');
        setCurrentDefaultRole(defaultRoleProp ?? 'workflow:viewer');
      } finally {
        setIsSavingDefaultRole(false);
      }
    },
    [
      orgId,
      resourceId,
      config.supportsDefaultRole,
      notification,
      defaultRoleProp,
      onDefaultRoleChange,
    ],
  );

  useEffect(() => {
    if (open && orgId && resourceId) {
      fetchPeople();
      fetchPendingRequests();
      setShowSuccessBanner(false);
      setEmailChips([]);
      setEmailInput('');

      // Keep any previously fetched members so autocomplete survives a
      // transient failure; a refetch only replaces them on success.
      setMembersStatus('loading');
      getAllMembersByOrg(orgId)
        .then((res) => {
          if (res.data) {
            setMembers(res.data);
            setMembersStatus('success');
          } else {
            setMembersStatus('error');
          }
        })
        .catch((err) => {
          Sentry.captureException(err, { extra: { orgId } });
          setMembersStatus('error');
        });
    }
  }, [open, orgId, resourceId, fetchPeople, fetchPendingRequests]);

  useEffect(() => {
    if (open) {
      setCurrentDefaultRole(defaultRoleProp ?? 'workflow:viewer');
    }
  }, [open, defaultRoleProp]);

  const addChipsFromInput = useCallback(() => {
    const parsed = parseEmails(emailInput);
    if (!parsed.length) return;

    const newChips = parsed.map((e) => ({
      email: e,
      isValid: validateEmail(e),
    }));

    setEmailChips((prev) => {
      const existing = new Set(prev.map((c) => c.email));
      return [...prev, ...newChips.filter((c) => !existing.has(c.email))];
    });
    setEmailInput('');
  }, [emailInput]);

  const removeChip = useCallback((email: string) => {
    setEmailChips((prev) => prev.filter((c) => c.email !== email));
  }, []);

  const suggestions = useMemo(() => {
    const query = emailInput.trim().toLowerCase();
    if (!query) return [];

    const alreadyAdded = new Set([
      ...emailChips.map((c) => c.email),
      ...people.map((p) => p.email.toLowerCase()),
    ]);

    return members.filter((m) => {
      const email = m.email.toLowerCase();
      if (alreadyAdded.has(email)) return false;
      return email.includes(query) || m.fullName?.toLowerCase().includes(query);
    });
  }, [emailInput, members, emailChips, people]);

  const selectSuggestion = useCallback((member: Member) => {
    const email = member.email.toLowerCase();
    setEmailChips((prev) => {
      if (prev.some((c) => c.email === email)) return prev;
      return [...prev, { email, isValid: true }];
    });
    setEmailInput('');
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, []);

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      notification.success('Link copied to clipboard');
    } catch {
      notification.error('Failed to copy link');
    }
  };

  const handleManagedRoleChange = async (
    email: string,
    newRole: 'viewer' | 'editor',
  ) => {
    const updateRole = config.api.updateRole;
    if (!orgId || !resourceId || !updateRole) return;
    setUpdatingRoleEmail(email);
    try {
      await updateRole(orgId, resourceId, email, newRole);
      notification.success(`Updated role for ${email} to ${newRole}`);
      fetchPeople();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { share_action: 'role_change', resource_type: resourceType },
        extra: { resourceId, orgId, email, newRole },
      });
      notification.error(`Failed to update role for ${email}`);
    } finally {
      setUpdatingRoleEmail(null);
    }
  };

  const handleRevoke = async (email: string) => {
    if (!orgId || !resourceId) return;
    setRevokingEmail(email);
    try {
      const result = await config.api.revoke(orgId, resourceId, email);

      if (result.status === 'revoked') {
        notification.success(`Removed access for ${email}`);
        fetchPeople();
      } else {
        notification.error(
          `Failed to remove access for ${email}: ${result.reason ?? 'Unknown error'}`,
        );
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { share_action: 'revoke', resource_type: resourceType },
        extra: { resourceId, orgId, email },
      });
      notification.error(`Failed to remove access for ${email}`);
    } finally {
      setRevokingEmail(null);
    }
  };

  const hasInvalidChips = emailChips.some((c) => !c.isValid);

  const handleInvite = async () => {
    // Commit any remaining text in the input as chips first
    if (emailInput.trim()) {
      addChipsFromInput();
      return;
    }

    if (hasInvalidChips) {
      notification.error('Remove invalid entries before sending invitations');
      return;
    }

    const allEmails = [...new Set(emailChips.map((c) => c.email))];
    if (!allEmails.length) {
      notification.error('Enter at least one email address');
      return;
    }

    if (!orgId || !resourceId) {
      notification.error('Missing organization or resource');
      return;
    }
    setIsInviting(true);
    try {
      const result = await config.api.share(
        orgId,
        resourceId,
        allEmails,
        collectionInviteRole,
      );

      const granted =
        result.shared_with?.filter((e) => e.status === 'granted').length ?? 0;
      const failed = result.shared_with?.filter((e) => e.status === 'failed');
      const already =
        result.shared_with?.filter((e) => e.status === 'already_has_access')
          .length ?? 0;

      // Show error notifications for failed emails
      if (failed?.length) {
        failed.forEach((f) =>
          notification.error(
            `Cannot invite ${f.email}: ${f.reason ?? 'Failed'}`,
          ),
        );
      }

      // Show success notification for granted emails
      if (granted) {
        notification.success(
          `Invited ${granted} ${granted === 1 ? 'person' : 'people'}`,
        );
      }

      // Show info notification for users who already have access
      if (already) {
        notification.info(
          `${already} ${already === 1 ? 'user already has' : 'users already have'} access`,
        );
      }

      // Clear input and chips, refresh list
      setEmailInput('');
      setEmailChips([]);
      fetchPeople();

      // The banner tells the owner to pass the link on, so it only applies
      // where a link conveys access; the invite toast covers the rest.
      if (granted > 0 && shareLink) {
        setShowSuccessBanner(true);
        try {
          await navigator.clipboard.writeText(shareLink);
        } catch {
          // Silent fail for clipboard copy
        }
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          share_action: 'invite',
          resource_type: resourceType,
        },
        extra: { resourceId, orgId },
      });
      notification.error('Failed to send invitations');
    } finally {
      setIsInviting(false);
    }
  };

  // Only trustworthy once both the access list and the member list actually
  // loaded: the exclusion set below is built from them, and a bulk grant
  // against an empty/stale snapshot would re-grant viewer over existing
  // (higher) roles — the single-grant POST overwrites.
  const canShareWithOrg =
    listStatus === 'success' && membersStatus === 'success';

  const handleShareWithOrg = async () => {
    if (
      !orgId ||
      !resourceId ||
      !config.supportsShareWithOrg ||
      !canShareWithOrg
    ) {
      return;
    }
    setIsSharingOrg(true);
    try {
      const currentUserEmail = user?.email?.toLowerCase();
      const excluded = new Set(
        people
          .map((p) => p.email.toLowerCase())
          .concat(currentUserEmail ? [currentUserEmail] : []),
      );
      const memberEmails = members
        .map((m) => m.email.toLowerCase())
        .filter((email) => email && !excluded.has(email));
      if (!memberEmails.length) {
        notification.info('All current members already have access');
        return;
      }
      const result = await config.api.share(
        orgId,
        resourceId,
        memberEmails,
        'viewer',
      );
      const granted =
        result.shared_with?.filter((e) => e.status === 'granted').length ?? 0;
      notification.success(
        `Shared with ${granted} ${granted === 1 ? 'member' : 'members'}`,
      );
      fetchPeople();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { share_action: 'share_org', resource_type: resourceType },
        extra: { resourceId, orgId },
      });
      notification.error('Failed to share with the organization');
    } finally {
      setIsSharingOrg(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-h-[90vh] flex flex-col sm:max-w-lg px-2!important'
        hideCloseButton
        aria-describedby={undefined}
      >
        <DialogHeader className='flex flex-row items-center justify-between gap-2 mb-3'>
          <DialogTitle className='text-lg font-semibold leading-none'>
            {title}
          </DialogTitle>
          <div className='flex items-center gap-1'>
            {shareLink && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='shrink-0 hover:text-foreground'
                onClick={handleCopyLink}
              >
                <Link className='size-4' />
                Copy Link
              </Button>
            )}
            <DialogClose asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='shrink-0 size-8 text-muted-foreground hover:text-foreground'
                aria-label='Close'
              >
                <X className='size-4' />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Invite: chip input + Invite button */}
        <div className='flex items-start gap-2'>
          <div className='relative flex-1'>
            <div
              className='flex min-h-9 max-h-32 cursor-text flex-wrap items-start gap-1 overflow-y-auto rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-within:ring-1 focus-within:ring-ring'
              onClick={() => inputRef.current?.focus()}
            >
              {emailChips.map((chip) => (
                <span
                  key={chip.email}
                  className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-0.5 pr-1.5 text-sm ${
                    chip.isValid
                      ? 'bg-muted'
                      : 'border-destructive/50 bg-destructive/10'
                  }`}
                >
                  {chip.isValid ? (
                    <UserAvatar email={chip.email} />
                  ) : (
                    <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/20'>
                      <AlertTriangle className='size-3 text-destructive' />
                    </span>
                  )}
                  <span className='max-w-[160px] truncate'>{chip.email}</span>
                  <button
                    type='button'
                    className='ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20'
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(chip.email);
                    }}
                    aria-label={`Remove ${chip.email}`}
                  >
                    <X className='size-3' />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type='text'
                placeholder={
                  emailChips.length === 0 ? 'Add people by name or email' : ''
                }
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setShowSuggestions(true);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (showSuggestions && suggestions.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setHighlightedIndex((i) =>
                        i < suggestions.length - 1 ? i + 1 : 0,
                      );
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightedIndex((i) =>
                        i > 0 ? i - 1 : suggestions.length - 1,
                      );
                      return;
                    }
                    if (e.key === 'Enter' && highlightedIndex >= 0) {
                      e.preventDefault();
                      selectSuggestion(suggestions[highlightedIndex]);
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setShowSuggestions(false);
                      return;
                    }
                  }
                  if (
                    (e.key === 'Enter' || e.key === ',' || e.key === ' ') &&
                    emailInput.trim()
                  ) {
                    e.preventDefault();
                    addChipsFromInput();
                    setShowSuggestions(false);
                  } else if (
                    e.key === 'Backspace' &&
                    !emailInput &&
                    emailChips.length > 0
                  ) {
                    removeChip(emailChips[emailChips.length - 1].email);
                  }
                }}
                onFocus={() => {
                  if (emailInput.trim()) setShowSuggestions(true);
                }}
                onBlur={() => {
                  // Delay to allow click on suggestion
                  setTimeout(() => {
                    setShowSuggestions(false);
                    if (emailInput.trim()) addChipsFromInput();
                  }, 200);
                }}
                className='min-w-[120px] flex-1 bg-transparent outline-none placeholder:text-muted-foreground h-[26px]'
                role='combobox'
                aria-expanded={showSuggestions && suggestions.length > 0}
                aria-autocomplete='list'
                aria-activedescendant={
                  highlightedIndex >= 0
                    ? `suggestion-${highlightedIndex}`
                    : undefined
                }
              />
            </div>

            {/* Autocomplete suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <ul
                ref={suggestionsRef}
                role='listbox'
                className='absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 shadow-md'
              >
                {suggestions.map((member, idx) => (
                  <li
                    key={member.email}
                    id={`suggestion-${idx}`}
                    role='option'
                    aria-selected={idx === highlightedIndex}
                    className={`flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm ${
                      idx === highlightedIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectSuggestion(member);
                    }}
                  >
                    <UserAvatar email={member.email} name={member.fullName} />
                    <div className='flex min-w-0 flex-col'>
                      {member.fullName && (
                        <span className='truncate text-sm font-medium'>
                          {member.fullName}
                        </span>
                      )}
                      <span className='truncate text-xs text-muted-foreground'>
                        {member.email}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {isRoleManaged && (
              <Select
                value={collectionInviteRole}
                onValueChange={(v) =>
                  setCollectionInviteRole(v as 'viewer' | 'editor')
                }
              >
                <SelectTrigger aria-label='Invite role' className='h-9'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='viewer'>Viewer</SelectItem>
                  <SelectItem value='editor'>Editor</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              size='default'
              onClick={handleInvite}
              disabled={
                isInviting ||
                (!emailInput.trim() && emailChips.length === 0) ||
                !orgId ||
                hasInvalidChips
              }
            >
              {isInviting ? (
                <>
                  <LoadingSpinner className='mr-1' />
                  Inviting…
                </>
              ) : (
                'Invite'
              )}
            </Button>
          </div>
        </div>

        {/* Share with the entire organization (context graphs) */}
        {config.supportsShareWithOrg && (
          <div className='flex items-center justify-between gap-3 rounded-md border border-input p-3'>
            <div className='min-w-0'>
              <p className='text-sm font-medium text-foreground'>
                Share with everyone in the organization
              </p>
              <p className='text-xs text-muted-foreground'>
                Grants every current member viewer access (a snapshot — members
                added later are not included automatically).
              </p>
            </div>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='shrink-0'
              onClick={handleShareWithOrg}
              disabled={isSharingOrg || !orgId || !canShareWithOrg}
            >
              {isSharingOrg ? (
                <>
                  <LoadingSpinner className='mr-1' />
                  Sharing…
                </>
              ) : (
                'Share with org'
              )}
            </Button>
          </div>
        )}

        {/* Success banner after invite */}
        {showSuccessBanner && (
          <div className='flex items-start gap-3 rounded-md border border-yellow-600 bg-yellow-50 dark:bg-yellow-900/80 p-3 text-sm'>
            <div className='flex-1'>
              <p className='font-semibold text-yellow-900 dark:text-yellow-200'>
                Link copied to clipboard
              </p>
              <p className='text-yellow-800 dark:text-yellow-200'>
                Share this link via Teams or email to give others access to this{' '}
                {noun}
              </p>
            </div>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='shrink-0 size-6 text-yellow-900 hover:text-yellow-950 hover:bg-yellow-100 dark:text-yellow-200 dark:hover:text-yellow-900'
              onClick={() => setShowSuccessBanner(false)}
              aria-label='Dismiss'
            >
              <X className='size-4' />
            </Button>
          </div>
        )}

        {/* Pending access requests */}
        {pendingRequests.length > 0 && (
          <div className='flex flex-col gap-2'>
            <h3 className='text-sm font-medium text-foreground'>
              Pending requests
              <span className='ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-orange-500 text-[11px] font-medium text-white'>
                {pendingRequests.length}
              </span>
            </h3>
            <ul className='flex flex-col gap-1'>
              {pendingRequests.map((req) => (
                <li
                  key={req.id}
                  className='flex items-center gap-3 rounded-md py-1.5 pr-2'
                >
                  <UserAvatar email={req.requester_email} />
                  <div className='min-w-0 flex-1'>
                    <span className='block truncate text-sm text-foreground'>
                      {req.requester_email}
                    </span>
                    {req.message && (
                      <span className='block truncate text-xs text-muted-foreground'>
                        {req.message}
                      </span>
                    )}
                  </div>
                  {reviewingRequestId === req.id ? (
                    <LoadingSpinner className='size-4 shrink-0' />
                  ) : (
                    <div className='flex items-center gap-1 shrink-0'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-7 text-green-600 hover:text-green-700 hover:bg-green-50'
                        aria-label={`Approve ${req.requester_email}`}
                        onClick={() => handleReviewRequest(req.id, 'approved')}
                      >
                        <Check className='size-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-7 text-destructive hover:bg-destructive/10'
                        aria-label={`Deny ${req.requester_email}`}
                        onClick={() => handleReviewRequest(req.id, 'denied')}
                      >
                        <X className='size-4' />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* People with access */}
        <div className='flex flex-col gap-2 overflow-hidden'>
          <h3 className='text-sm font-medium text-foreground'>
            People with access
          </h3>
          {listStatus === 'loading' ? (
            <div className='flex items-center gap-2 py-2 text-muted-foreground'>
              <LoadingSpinner />
              <span className='text-sm'>Loading…</span>
            </div>
          ) : listStatus === 'error' ? (
            <div className='flex items-center gap-2 py-2'>
              <p className='text-sm text-muted-foreground'>
                Couldn&apos;t load the access list.
              </p>
              <Button
                variant='outline'
                size='sm'
                className='h-7 text-xs'
                onClick={() => fetchPeople()}
              >
                Try again
              </Button>
            </div>
          ) : people.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No one else has access yet.
            </p>
          ) : (
            <ul className='flex flex-col gap-1 overflow-y-auto'>
              {people.map((entry) => (
                <li
                  key={entry.email}
                  className='flex items-center gap-3 rounded-md py-1.5 pr-2'
                >
                  <UserAvatar email={entry.email} />
                  <span className='min-w-0 flex-1 truncate text-sm text-foreground'>
                    {entry.email}
                  </span>
                  {isRoleManaged && !isOwnerRole(entry.role) ? (
                    <Select
                      value={entry.role}
                      disabled={updatingRoleEmail === entry.email}
                      onValueChange={(v) =>
                        handleManagedRoleChange(
                          entry.email,
                          v as 'viewer' | 'editor',
                        )
                      }
                    >
                      <SelectTrigger
                        aria-label={`Role for ${entry.email}`}
                        className='h-7 shrink-0 text-xs'
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='viewer'>Viewer</SelectItem>
                        <SelectItem value='editor'>Editor</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className='shrink-0 text-sm text-muted-foreground'>
                      {entry.role && displayRole(entry.role)}
                    </span>
                  )}
                  {!isOwnerRole(entry.role) ? (
                    revokingEmail === entry.email ? (
                      <LoadingSpinner className='size-4 shrink-0' />
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='shrink-0 size-7 text-muted-foreground hover:text-foreground'
                            aria-label={`More options for ${entry.email}`}
                          >
                            <MoreVertical className='size-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            className='text-destructive focus:text-destructive'
                            onClick={() => handleRevoke(entry.email)}
                          >
                            Remove access
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  ) : (
                    <div className='size-7 shrink-0' />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Default access role */}
        {config.supportsDefaultRole && (
          <div className='flex flex-col gap-2 border-t pt-3'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-sm font-medium text-foreground'>
                  Default access
                </h3>
                <p className='text-xs text-muted-foreground'>
                  Access level for all org members
                </p>
              </div>
              <DefaultAccessDropdown
                value={currentDefaultRole ?? 'workflow:viewer'}
                onChange={handleDefaultRoleChange}
                showClientDataWarning={showClientDataWarning}
                showWarningBanner={false}
                disabled={isSavingDefaultRole}
              />
            </div>
            {showClientDataWarning &&
              currentDefaultRole === 'workflow:editor' && (
                <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200'>
                  <AlertTriangle className='size-4 shrink-0 mt-0.5' />
                  <span>
                    This workflow is accessible to all organization members.
                    Ensure it does not contain client-specific or sensitive
                    data.
                  </span>
                </div>
              )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
